import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { Note } from 'shared/models';
import {
  APIError,
  NotesAPIService,
  OthersBookshelfOpenerService,
} from 'shared/services';
import { AlertService, MenuPositionBottomLeft, ToastService } from 'shared/ui';

enum Tab {
  Inbox,
  Sent,
}

@Component({
  selector: 'lib-note-list',
  templateUrl: './note-list.component.html',
  styleUrl: './note-list.component.scss',
})
export class NoteListComponent implements OnInit {
  @Input() isViewer!: boolean;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.isViewer) {
      return;
    }

    const target = event.target as HTMLAnchorElement;

    if (target.tagName === 'A' && target.href) {
      // memo url과 memo에 bid 추출
      const memoUrl = target.href;
      const regex = /(https?:\/\/[^/:]+(?::\d+)?)\/?(@[^/]+)?/;
      const match = memoUrl.match(regex);
      const domainRegex = /http[s]?:\/\/(?<domain>[^/]+)/;
      const domainMatch = memoUrl.match(domainRegex);

      const origin = window.location.origin; // 현재 호스트 주소
      const urlBid = this.router.url.split('/')[1]; // 현재 URL에 bid 추출

      if (!match) {
        return;
      }

      if (!domainMatch) {
        return;
      }

      if (!domainMatch.groups) {
        return;
      }

      if (
        (origin === match[1] && urlBid === match[2]) ||
        (/(?:^|\.)buk\.io$/.test(domainMatch.groups['domain']) &&
          urlBid === match[2])
      ) {
        // https://buk.io 인 경우 새로운 창에서 메인 페이지로 이동하도록 분기처리
        if (memoUrl.replace(match[1], '') === '/') {
          return;
        }

        // 조건에 만족할경우 a 태그 기본 동작 방지 및 앵귤러 navigate로 새로고침 없이 이동하도록 수정
        event.preventDefault();

        if (memoUrl.replace(match[1], '') === '/') {
          return;
        }

        this.contentUrlClick.emit(memoUrl.replace(match[1], ''));
      }
    }

    return;
  }
  @ViewChild('listElem') private _listElem?: ElementRef<HTMLDivElement>;

  @Output() responseMenuClick = new EventEmitter<{
    userId: string;
    nickname: string;
  }>();
  @Output() contentUrlClick = new EventEmitter<string>();

  public _inboxNotes?: Note[];
  public _inboxTotal?: number;

  public _sentNotes?: Note[];
  public _sentTotal?: number;

  public _selectedNoteIndex = 0;

  private _isLoading = false;

  public readonly _moreMenuPosition = MenuPositionBottomLeft;

  constructor(
    private _notesAPIService: NotesAPIService,
    private _toastService: ToastService,
    private _alertService: AlertService,
    private _othersBookshelfOpenerService: OthersBookshelfOpenerService,
    private router: Router
  ) {}

  public readonly _Tab = Tab;
  public _currentTab = Tab.Inbox;

  get _notes(): Note[] | undefined {
    return this._currentTab === Tab.Inbox ? this._inboxNotes : this._sentNotes;
  }

  get _notesTotal(): number | undefined {
    return this._currentTab === Tab.Inbox ? this._inboxTotal : this._sentTotal;
  }

  ngOnInit(): void {
    this._getInbox(true);
    this._getSent(true);
  }

  private _getInbox(reset: boolean): void {
    this._isLoading = true;

    this._notesAPIService
      .getInbox(reset ? 0 : this._inboxNotes?.length ?? 0, 30)
      .subscribe((response) => {
        this._isLoading = false;
        this._inboxNotes = (reset ? [] : this._inboxNotes ?? []).concat(
          response.items
        );
        this._inboxTotal = response.total;
      });
  }

  private _getSent(reset: boolean): void {
    this._isLoading = true;

    this._notesAPIService
      .getSent(reset ? 0 : this._sentNotes?.length ?? 0, 30)
      .subscribe((response) => {
        this._isLoading = false;
        this._sentNotes = (reset ? [] : this._sentNotes ?? []).concat(
          response.items
        );
        this._sentTotal = response.total;
      });
  }

  refresh(): void {
    this._getInbox(true);
    this._getSent(true);

    if (this._listElem) {
      this._listElem.nativeElement.scrollTop = 0;
    }
  }

  _onScrollEnd(): void {
    if (this._isLoading) {
      return;
    }

    if (this._notes && this._notes.length === this._notesTotal) {
      return;
    }

    this._currentTab === Tab.Inbox
      ? this._getInbox(false)
      : this._getSent(false);
  }

  _onSelectTab(tab: Tab): void {
    if (this._currentTab === tab) {
      return;
    }

    this._currentTab = tab;
    this._selectedNoteIndex = 0;

    if (this._listElem) {
      this._listElem.nativeElement.scrollTop = 0;
    }
  }

  _onResponseMenuClick(): void {
    const note = this._notes?.[this._selectedNoteIndex];

    if (!note) {
      return;
    }

    this.responseMenuClick.emit({
      userId: note.user.id,
      nickname: note.user.nickname,
    });
  }

  _onDeleteMenuClick(): void {
    const note = this._notes?.[this._selectedNoteIndex];

    if (!note) {
      return;
    }

    this._alertService
      .openConfirm(undefined, '쪽지를 삭제하시겠습니까?', '삭제하기')
      .afterClosed()
      .pipe(
        filter((ok) => !!ok),
        switchMap(() => {
          return this._notesAPIService.delete(note.id);
        })
      )
      .subscribe({
        next: () => {
          this._sentNotes = this._sentNotes?.filter((n) => n.id !== note.id);
          this._inboxNotes = this._inboxNotes?.filter((n) => n.id !== note.id);
          this._sentTotal = this._sentNotes?.length;
          this._inboxTotal = this._inboxNotes?.length;

          if (!this._notes?.[this._selectedNoteIndex]) {
            this._selectedNoteIndex = 0;
          }
        },
        error: (error: APIError) => {
          this._toastService.openWarning(error.message);
        },
      });
  }

  _onUserClick(): void {
    const note = this._notes?.[this._selectedNoteIndex];

    if (!note) {
      return;
    }

    this._othersBookshelfOpenerService.open(note.user.id);
  }
}
